import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { mergeHits, pickCountLine, pickQuery } from '../lib/contactPick.js';

/**
 * ehr#208 - pick another client's chart (Demographics → Family & Contacts → Linked chart).
 * Shows the chosen client with Change / Remove; otherwise a search box listing matching
 * clients (GET /contacts?category=client&search=…&exclude=<this chart>).
 * ehr#636 - pages of 25 from the server, which searches every client; Show more (or
 * scrolling to the bottom) fetches the next page, and the footer says how many of how many.
 * Arrow keys move through the list and Enter takes the highlighted client.
 */
export interface PickedContact {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
}
interface Hit {
  id: string;
  firstName: string;
  lastName: string;
}
interface Page {
  items?: Hit[];
  nextCursor?: string | null;
  total?: number;
}
const nameOf = (c: { firstName: string; lastName: string }) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Unnamed client';

export function ContactPickField({
  value,
  excludeId,
  onChange,
}: {
  /** The linked chart: its id and the name saved with it ('' = none). */
  value: { id: string; name: string };
  excludeId: string;
  onChange: (c: PickedContact | null) => void;
}) {
  const [editing, setEditing] = useState(!value.id);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [more, setMore] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Bumped on every new search so a late page from an older one is ignored.
  const gen = useRef(0);

  useEffect(() => setEditing(!value.id), [value.id]);
  useEffect(() => {
    if (!open) return;
    const g = ++gen.current;
    const t = setTimeout(() => {
      api
        .get<Page>(pickQuery(text, excludeId))
        .then((r) => {
          if (g !== gen.current) return;
          setHits(mergeHits([], r.items ?? [], excludeId));
          setCursor(r.nextCursor ?? null);
          setTotal(typeof r.total === 'number' ? r.total : null);
          setActive(0);
          panelRef.current?.scrollTo?.(0, 0);
        })
        .catch(() => {
          if (g !== gen.current) return;
          setHits([]);
          setCursor(null);
          setTotal(null);
        });
    }, 200);
    return () => clearTimeout(t);
  }, [text, open, excludeId]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const loadMore = () => {
    if (!cursor || more) return;
    const g = gen.current;
    setMore(true);
    api
      .get<Page>(pickQuery(text, excludeId, cursor))
      .then((r) => {
        if (g !== gen.current) return;
        setHits((h) => mergeHits(h ?? [], r.items ?? [], excludeId));
        setCursor(r.nextCursor ?? null);
        if (typeof r.total === 'number') setTotal(r.total);
      })
      .catch(() => undefined)
      .finally(() => setMore(false));
  };

  const pick = (c: Hit) => {
    onChange({ id: c.id, name: nameOf(c), firstName: c.firstName, lastName: c.lastName });
    setOpen(false);
    setText('');
  };
  const moveTo = (i: number) => {
    setActive(i);
    const row = panelRef.current?.querySelectorAll<HTMLElement>('.cpick-row')[i];
    row?.scrollIntoView?.({ block: 'nearest' });
    if (hits && i >= hits.length - 3) loadMore();
  };

  if (!editing && value.id) {
    return (
      <div className="cpick-chosen">
        <span className="cpick-name">{value.name || 'Linked client'}</span>
        <button
          type="button"
          className="cpick-link"
          onClick={() => {
            setEditing(true);
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
        >
          Change
        </button>
        <button type="button" className="cpick-link" onClick={() => onChange(null)}>
          Remove
        </button>
      </div>
    );
  }
  const countLine = hits ? pickCountLine(hits.length, total, !!text.trim()) : '';
  return (
    <div className="cpick" ref={wrapRef}>
      <input
        ref={inputRef}
        type="search"
        value={text}
        placeholder="Search this person’s chart by name…"
        aria-label="Search clients to link"
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'ArrowDown' && hits?.length) {
            e.preventDefault();
            setOpen(true);
            moveTo(Math.min(active + 1, hits.length - 1));
          }
          if (e.key === 'ArrowUp' && hits?.length) {
            e.preventDefault();
            moveTo(Math.max(active - 1, 0));
          }
          if (e.key === 'Enter' && hits?.[active]) {
            e.preventDefault();
            pick(hits[active]);
          }
        }}
      />
      {open && (
        <div className="cpick-panel" role="listbox" aria-label="Clients">
          <div
            className="cpick-list"
            ref={panelRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) loadMore();
            }}
          >
            {hits === null ? (
              <div className="cpick-empty">Loading…</div>
            ) : hits.length === 0 ? (
              <div className="cpick-empty">No clients match.</div>
            ) : (
              <>
                {hits.map((c, i) => (
                  <button
                    key={c.id}
                    type="button"
                    role="option"
                    aria-selected={i === active}
                    className={i === active ? 'cpick-row active' : 'cpick-row'}
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(c)}
                  >
                    {nameOf(c)}
                  </button>
                ))}
                {cursor && (
                  <button
                    type="button"
                    className="cpick-more"
                    disabled={more}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={loadMore}
                  >
                    {more ? 'Loading…' : 'Show more clients'}
                  </button>
                )}
              </>
            )}
          </div>
          {countLine && <div className="cpick-count">{countLine}</div>}
        </div>
      )}
      {value.id && (
        <button type="button" className="cpick-link cpick-cancel" onClick={() => setEditing(false)}>
          Keep {value.name || 'the linked client'}
        </button>
      )}
    </div>
  );
}
